import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Sku, SkuType, UpdateSkuInput, CreateSkuInput } from '../../../types';

interface SkuOverviewTabProps {
  sku: Sku | null;
  mode: 'view' | 'create';
  onSave: (patch: Partial<UpdateSkuInput> | Partial<CreateSkuInput>) => void;
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </Label>
      {children}
    </div>
  );
}

export function SkuOverviewTab({ sku, mode: _mode, onSave }: SkuOverviewTabProps) {
  const [skuCode, setSkuCode] = useState(sku?.sku ?? '');
  const [type, setType] = useState<SkuType>(sku?.type ?? 'inventory');
  const [nameEn, setNameEn] = useState(sku?.nameEn ?? '');
  const [nameFr, setNameFr] = useState(sku?.nameFr ?? '');
  const [descEn, setDescEn] = useState(sku?.descriptionEn ?? '');
  const [descFr, setDescFr] = useState(sku?.descriptionFr ?? '');
  const [manufacturerName, setManufacturerName] = useState(sku?.manufacturerName ?? '');
  const [model, setModel] = useState(sku?.model ?? '');
  const [value, setValueField] = useState(sku?.value ?? '');
  const [cost, setCost] = useState(sku?.cost ?? '');
  const [activated, setActivated] = useState(sku?.activated ?? true);
  const [saleable, setSaleable] = useState(sku?.saleable ?? true);

  const baselineRef = useRef({
    skuCode,
    type,
    nameEn,
    nameFr,
    descEn,
    descFr,
    manufacturerName,
    model,
    value,
    cost,
    activated,
    saleable,
  });

  // CRITICAL: guard on sku.id only — server invalidations re-emit the same id
  // with same values, which would clobber in-flight edits.
  useEffect(() => {
    if (!sku) return;
    setSkuCode(sku.sku);
    setType(sku.type);
    setNameEn(sku.nameEn);
    setNameFr(sku.nameFr);
    setDescEn(sku.descriptionEn ?? '');
    setDescFr(sku.descriptionFr ?? '');
    setManufacturerName(sku.manufacturerName ?? '');
    setModel(sku.model ?? '');
    setValueField(sku.value ?? '');
    setCost(sku.cost ?? '');
    setActivated(sku.activated);
    setSaleable(sku.saleable);
    baselineRef.current = {
      skuCode: sku.sku,
      type: sku.type,
      nameEn: sku.nameEn,
      nameFr: sku.nameFr,
      descEn: sku.descriptionEn ?? '',
      descFr: sku.descriptionFr ?? '',
      manufacturerName: sku.manufacturerName ?? '',
      model: sku.model ?? '',
      value: sku.value ?? '',
      cost: sku.cost ?? '',
      activated: sku.activated,
      saleable: sku.saleable,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku?.id]);

  const blurIfChanged = (
    key: keyof typeof baselineRef.current,
    current: unknown,
    fieldName: string,
  ) => () => {
    if (current === baselineRef.current[key]) return;
    baselineRef.current = { ...baselineRef.current, [key]: current as never };
    onSave({ [fieldName]: current } as Partial<UpdateSkuInput>);
  };

  const valueChange = (
    key: keyof typeof baselineRef.current,
    current: unknown,
    fieldName: string,
  ) => {
    baselineRef.current = { ...baselineRef.current, [key]: current as never };
    onSave({ [fieldName]: current } as Partial<UpdateSkuInput>);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="SKU code" htmlFor="sku-code">
          <Input
            id="sku-code"
            value={skuCode}
            onChange={(e) => setSkuCode(e.target.value)}
            onBlur={blurIfChanged('skuCode', skuCode, 'sku')}
            className="font-mono"
          />
        </Field>
        <Field label="Type">
          <Select
            value={type}
            onValueChange={(v) => {
              setType(v as SkuType);
              valueChange('type', v, 'type');
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inventory">Inventory</SelectItem>
              <SelectItem value="non_inventory">Non-Inventory</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="English name" htmlFor="name-en">
        <Input
          id="name-en"
          value={nameEn}
          onChange={(e) => setNameEn(e.target.value)}
          onBlur={blurIfChanged('nameEn', nameEn, 'nameEn')}
        />
      </Field>

      <Field label="French name" htmlFor="name-fr">
        <Input
          id="name-fr"
          value={nameFr}
          onChange={(e) => setNameFr(e.target.value)}
          onBlur={blurIfChanged('nameFr', nameFr, 'nameFr')}
        />
      </Field>

      <Field label="English description" htmlFor="desc-en">
        <Textarea
          id="desc-en"
          rows={3}
          value={descEn}
          onChange={(e) => setDescEn(e.target.value)}
          onBlur={blurIfChanged('descEn', descEn, 'descriptionEn')}
        />
      </Field>

      <Field label="French description" htmlFor="desc-fr">
        <Textarea
          id="desc-fr"
          rows={3}
          value={descFr}
          onChange={(e) => setDescFr(e.target.value)}
          onBlur={blurIfChanged('descFr', descFr, 'descriptionFr')}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Manufacturer" htmlFor="manuf">
          <Input
            id="manuf"
            value={manufacturerName}
            onChange={(e) => setManufacturerName(e.target.value)}
            onBlur={blurIfChanged('manufacturerName', manufacturerName, 'manufacturerName')}
          />
        </Field>
        <Field label="Model" htmlFor="model">
          <Input
            id="model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onBlur={blurIfChanged('model', model, 'model')}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Price" htmlFor="value">
          <Input
            id="value"
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValueField(e.target.value)}
            onBlur={blurIfChanged('value', value, 'value')}
          />
        </Field>
        <Field label="Cost" htmlFor="cost">
          <Input
            id="cost"
            type="number"
            step="0.01"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            onBlur={blurIfChanged('cost', cost, 'cost')}
          />
        </Field>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={activated}
            onCheckedChange={(v) => {
              const b = v === true;
              setActivated(b);
              valueChange('activated', b, 'activated');
            }}
          />
          <span className="text-sm">Activated</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={saleable}
            onCheckedChange={(v) => {
              const b = v === true;
              setSaleable(b);
              valueChange('saleable', b, 'saleable');
            }}
          />
          <span className="text-sm">Saleable</span>
        </label>
      </div>
    </div>
  );
}
